import {
	Button,
	Form,
	Table,
	TablePaginationConfig,
	Typography,
} from 'antd/lib';
import { SorterResult } from 'antd/lib/table/interface';
import Link from 'antd/lib/typography/Link';
import Title from 'antd/lib/typography/Title';
import {
	DeployActionEnum,
	DeployStatusEnum,
	DeviceStatusEnum,
	DevicesSortOptions,
	SoftwareAvailableDTO,
	SoftwareNameEnum,
	SortDirectionOptions,
	DeviceDTO,
	DeviceWithCamerasDTO,
} from 'api/api_code';
import {
	DeviceDetailsEndpoints,
	DevicesEndpoints,
	SoftwaresEndpoints,
} from 'api/axios/axiosAPIGroups';
import clsx from 'clsx';
import { Icon } from 'components/UI/Icon/Icon';
import { IconNames } from 'components/UI/Icon/icons';
import { ConfirmationModal } from 'components/UI/Modals/ConfirmationModal';
import {
	IconType,
	NotificationModal,
} from 'components/UI/Modals/NotificationModal';
import { PAIRING_STEPS, pairingSteps } from 'core/pairing/pairing';
import {
	getFromSessionStorage,
	setToSessionStorage,
} from 'helpers/storageUtils';
import { useHTTPRequestHandler } from 'hooks/useHTTPRequestHandler';
import { usePairingContext } from 'hooks/usePairingContext';
import { useSession } from 'hooks/useSession';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLoading } from 'hooks/useLoading';
import getFirmwareStatus from '../helpers/getFirmwareStatus';
import useCameraFirmwareActions, {
	FirmwareAction,
} from '../hooks/useCameraFirmwareStatus';
import { usePairing } from '../pairing/components/deploy/hooks/usePairing';
import styles from './CameraList.module.css';
import { CameraStatus } from './CameraStatus';
import FirmwareField, { FirmwareStatus } from './FirmwareField';
import { isProBroadcastSelector, useCameraStore } from 'stores/camera.store';
import { SearchInput, CloudIcon, Tooltip } from '@atvlibs/atvdesignsystem';
import { useDebouncedCallback } from 'use-debounce';
import CameraListFooter from './CameraListFooter';
import Image from 'next/image';
import { getCameraLicense, getIsServerless } from '../../helpers/camera';
import { getMatchingStatusIds } from 'helpers/deviceUtils';

const { Text } = Typography;

type TableState = {
	search: string;
	status: string[];
	sortBy: string | undefined;
	sortDir: 'asc' | 'desc' | undefined;
	pageSize: number;
	currentPage: number;
};

function CamerasList() {
	const [openModal, setOpenModal] = useState<{
		open: boolean;
		type: 'multi' | 'single';
	}>({
		open: false,
		type: 'single',
	});
	const [data, setData] = useState<Record<number, DeviceDTO[]>>({});
	const [total, setTotal] = useState(0);
	const [softwareAvailable, setSoftwareAvailable] =
		useState<SoftwareAvailableDTO>({
			name: '',
			version: { version_number: '' },
		});
	const [selectedRowKeys, setSelectedRowKeys] = useState<
		Record<number, string[]>
	>({ 1: [] });
	const [selectionCount, setSelectionCount] = useState(0);
	const [upgradeDevices, setUpgradeDevices] = useState([]);
	const [searchInputValue, setSearchInputValue] = useState('');
	const [tableState, setTableState] = useState<TableState | undefined>(
		undefined
	);
	const { setLoading } = useLoading();

	const {
		currentStep,
		setCurrentStep,
		setPairingModalState,
		pairedDeviceUUID,
		softwareInstallationState,
		setOpenLicensingModal,
	} = usePairingContext();
	const { requestHandler } = useHTTPRequestHandler();
	const { deploySoftwareInstallation } = usePairing();
	const {
		firmwareActionInProgress,
		cameraFirmwareAction,
		resetFirmwareActions,
	} = useCameraFirmwareActions();
	const { closeWizardSession } = useSession();
	const { t } = useTranslation('cameras');
	const { t: tCommon } = useTranslation('common');
	const router = useRouter();
	const isTableStateInitialized = useRef(false);

	const device_uuid = getFromSessionStorage('device_uuid');

	// Initialize tableState from router.query once router is ready
	useEffect(() => {
		if (!router.isReady) return;
		const search = (router.query.search as string) || '';
		setSearchInputValue(search);
		setTableState({
			search,
			status: router.query.status
				? ([] as string[]).concat(router.query.status as string | string[])
				: [],
			sortBy: (router.query.sortBy as string) || undefined,
			sortDir: (router.query.sortDir as 'asc' | 'desc') || undefined,
			pageSize: Number(router.query.pageSize) || 10,
			currentPage: Number(router.query.page) || 1,
		});

		setLoading(true);

		// Get software available
		requestHandler({
			requestPromise: SoftwaresEndpoints.getSoftwareSoftwaresSoftwareGet(
				SoftwareNameEnum.Automatictv
			),
			onOkCallback: (data) => setSoftwareAvailable(data),
			autoLoading: false,
		});
	}, [router.isReady]);

	useEffect(() => {
		if (tableState === undefined) return;

		// Sync tableState with URL (skip on first init since URL is already correct)
		if (!isTableStateInitialized.current) {
			isTableStateInitialized.current = true;
		} else {
			router.replace(
				{
					pathname: router.pathname,
					query: {
						...(tableState?.search && { search: tableState?.search }),
						...(tableState?.status.length && { status: tableState?.status }),
						...(tableState?.sortBy && {
							sortBy: tableState?.sortBy,
							sortDir: tableState?.sortDir,
						}),
						page: tableState?.currentPage,
						pageSize: tableState?.pageSize,
					},
				},
				undefined,
				{ shallow: true }
			);
		}

		// Fetch cameras list when tableState changes
		requestHandler({
			requestPromise: DevicesEndpoints.getDevicesListDevicesGet(
				tableState?.search,
				tableState?.status,
				tableState?.sortBy as DevicesSortOptions,
				tableState?.sortDir as SortDirectionOptions,
				tableState?.currentPage,
				tableState?.pageSize
			),
			onOkCallback: (cameras) => {
				setData((prev) => ({
					...prev,
					[tableState?.currentPage]: cameras.items,
				}));
				setTotal(cameras.total);
				setLoading(false);
			},
			autoLoading: !Boolean(tableState?.search),
		});
	}, [tableState]);

	// Polling refresh every 30s
	useEffect(() => {
		const intervalTimer = setInterval(() => {
			requestHandler({
				requestPromise: DevicesEndpoints.getDevicesListDevicesGet(
					tableState?.search,
					tableState?.status,
					tableState?.sortBy as DevicesSortOptions,
					tableState?.sortDir as SortDirectionOptions,
					tableState?.currentPage,
					tableState?.pageSize
				),
				onOkCallback: (cameras) => {
					setData((prev) => ({
						...prev,
						[tableState?.currentPage]: cameras.items,
					}));
					setTotal(cameras.total);
					resetFirmwareActions();
				},
				autoLoading: false,
			});
		}, 30 * 1000);
		return () => clearInterval(intervalTimer);
	}, [tableState]);

	// Cleanup wizard session on mount
	useEffect(() => {
		const device_uuid = getFromSessionStorage('device_uuid');
		const wizardToken = getFromSessionStorage('session_token');
		if (wizardToken !== null && device_uuid !== null) {
			closeWizardSession(device_uuid);
		}
		sessionStorage.removeItem('device_uuid');
		sessionStorage.removeItem('field_uuid');
		sessionStorage.removeItem('camera-store');
		sessionStorage.removeItem('wizard_session_request_id');
	}, []);

	const accessToCamDetails = useCallback(
		(uuid: string) => {
			requestHandler({
				requestPromise:
					DeviceDetailsEndpoints.getDeviceDetailDevicesDeviceUuidGet(uuid),
				onOkCallback: (camera: DeviceWithCamerasDTO) => {
					useCameraStore.setState(
						(state) => {
							state.cameraConfig = { ...state.cameraConfig, ...camera };
							state.cameraConfigBE = camera;
						},
						false,
						'camera/accessCameraDetails'
					);
				},
			});
		},
		[device_uuid]
	);

	const setTextStyles = (status: DeviceStatusEnum) =>
		clsx({
			[styles.soft]: status === DeviceStatusEnum.Offline,
			[styles.velvet]: status === DeviceStatusEnum.Recording,
		});

	const getIsActionInProgress = useCallback(
		(recordUuid: string) => firmwareActionInProgress[recordUuid],
		[firmwareActionInProgress]
	);

	const columns = useMemo(() => {
		const getSortOrder = (field: string): 'ascend' | 'descend' | null =>
			tableState?.sortBy === field
				? tableState?.sortDir === 'asc'
					? 'ascend'
					: 'descend'
				: null;

		return [
			{
				title: t('columnNames.name'),
				dataIndex: 'name',
				key: 'name',
				width: '22%',
				sorter: true,
				sortOrder: getSortOrder('name'),
				render: (_, record) => {
					const isServerless = getIsServerless(record.producer);
					return (
						<div className={styles.nameCol}>
							<Text className={setTextStyles(record.status)}>
								{record.name}
								{isServerless ? (
									<Tooltip title={tCommon('serverlessSystem')}>
										<span style={{ marginLeft: '8px' }}>
											<CloudIcon size={16} />
										</span>
									</Tooltip>
								) : (
									''
								)}
							</Text>
							<Text style={{ color: 'var(--soft-500)', fontWeight: 400 }}>
								{record.producer?.serial_number}
							</Text>
						</div>
					);
				},
			},
			{
				title: t('columnNames.organization'),
				dataIndex: 'org_name',
				width: '20%',
				key: 'org_name',
				sorter: true,
				sortOrder: getSortOrder('org_name'),
				render: (_, record) => (
					<Text
						className={setTextStyles(record.status)}
						data-testid="camera_organization"
					>
						{record.organization?.name}
					</Text>
				),
			},
			{
				title: t('columnNames.firmware'),
				dataIndex: 'software_installed',
				key: 'firmware',
				width: '19%',
				sorter: true,
				sortOrder: getSortOrder('software_installed'),
				render: (_, record) => {
					const { version, status } = getFirmwareStatus({
						device: record,
						softwareAvailable,
					});
					const isServerless = getIsServerless(record.producer);
					const firmwareAction = {
						[FirmwareStatus.UPGRADE]: (event) => {
							pairedDeviceUUID.current = record.uuid;
							setOpenModal({ open: true, type: 'single' });
							event.stopPropagation();
						},
						[FirmwareStatus.UPGRADE_FAILED]: (event) => {
							pairedDeviceUUID.current = record.uuid;
							setOpenModal({ open: true, type: 'single' });
							event.stopPropagation();
						},
						[FirmwareStatus.INSTALL]: (event) => {
							cameraFirmwareAction[FirmwareStatus.INSTALL]({
								cameraUUID: record.uuid,
							});
							event.stopPropagation();
						},
						[FirmwareStatus.INSTALL_FAILED]: (event) => {
							cameraFirmwareAction[FirmwareStatus.INSTALL]({
								cameraUUID: record.uuid,
							});
							event.stopPropagation();
						},
					};
					const isActionInProgress = getIsActionInProgress(record.uuid);
					const upgradeInProgress =
						isActionInProgress && FirmwareStatus.UPGRADING;
					return (
						<FirmwareField
							version={version}
							newVersion={softwareAvailable?.version?.version_number}
							action={firmwareAction[status]}
							firmwareStatus={upgradeInProgress || status}
							deviceStatus={record.status}
							translate={t}
							disable={
								record.status === DeviceStatusEnum.Offline ||
								record.status === DeviceStatusEnum.Recording
							}
							actionInProgress={isActionInProgress ?? false}
							isServerless={isServerless}
						/>
					);
				},
			},
			{
				title: t('columnNames.status'),
				dataIndex: 'status',
				width: '15%',
				key: 'status',
				sorter: true,
				sortOrder: getSortOrder('status'),
				render: (_, record) => (
					<CameraStatus
						camera={record}
						setOpenLicensingModal={setOpenLicensingModal}
					/>
				),
			},
			{
				title: t('columnNames.license'),
				key: 'license',
				width: '24%',
				dataIndex: 'license',
				sorter: true,
				sortOrder: getSortOrder('license'),
				render: (_, record) => {
					const isActionInProgress = getIsActionInProgress(record.uuid);
					const caseOffline = record.status === DeviceStatusEnum.Offline;
					const caseRecording = record.status === DeviceStatusEnum.Recording;
					const caseUpgrading =
						record.software_installing?.find(
							(item) =>
								item.version?.software?.name === SoftwareNameEnum.Automatictv
						)?.action === DeployActionEnum.Upgrade;
					const caseInstalling =
						record.software_installing?.find(
							(item) =>
								item.version?.software?.name === SoftwareNameEnum.Automatictv
						)?.action === DeployActionEnum.Install;
					const caseNotLicensed = record.status === DeviceStatusEnum.Unlicensed;
					const caseCameraDisconnected =
						record.status === DeviceStatusEnum.CameraUnavailable;
					const caseNotInstalled =
						record.status === DeviceStatusEnum.NotInstalled;
					// Pro/Broadcast systems: keep them accessible while the camera is unavailable
					const isProBroadcast = isProBroadcastSelector({
						cameraConfig: record,
					} as Parameters<typeof isProBroadcastSelector>[0]);
					const disableCameraActions =
						caseOffline ||
						caseUpgrading ||
						caseInstalling ||
						caseNotLicensed ||
						caseRecording ||
						(caseCameraDisconnected && !isProBroadcast) ||
						caseNotInstalled ||
						isActionInProgress;
					return (
						<div
							className={`${styles.settings} ${setTextStyles(record.status)}`}
						>
							{getCameraLicense(record.producer?.license_type)}
							<Tooltip
								placement="topRight"
								title={
									caseOffline
										? t('tooltip.offline')
										: caseUpgrading
											? t('tooltip.upgrading')
											: caseInstalling
												? t('tooltip.installing')
												: caseCameraDisconnected && !isProBroadcast
													? t('tooltip.disconnectedError')
													: caseRecording
														? t('tooltip.recording')
														: caseNotInstalled
															? t('tooltip.notInstalled')
															: t('tooltip.camConfig')
								}
								className={styles['tooltipCameraList-config']}
							>
								<Button
									type="link"
									className={`${styles.hiddenButtons} ${
										disableCameraActions ? styles.disabledSet : styles.available
									}`}
									{...(!disableCameraActions && {
										onClick: (event) => {
											accessToCamDetails(record.uuid);
											setToSessionStorage('lens-size', record.lens_size);
											setToSessionStorage(
												'license-type',
												record.producer.license_type
											);
											event.stopPropagation();
											router.push({
												pathname: '/camera/[device_uuid]/settings',
												query: { device_uuid: record.uuid, previous: '/' },
											});
										},
									})}
								>
									<span className={styles.set}>
										<Icon component={IconNames.SETTINGS} size={24} />
									</span>
								</Button>
							</Tooltip>
						</div>
					);
				},
			},
		];
	}, [
		firmwareActionInProgress,
		data,
		softwareAvailable,
		getIsActionInProgress,
		tableState,
	]);

	const handleSelect = (record, selected) => {
		if (selected) {
			setSelectedRowKeys((keys) => ({
				...keys,
				[tableState?.currentPage]: [
					...(keys[tableState?.currentPage]?.length
						? keys[tableState?.currentPage]
						: []),
					record.uuid,
				],
			}));
		} else {
			setSelectedRowKeys((keys) => {
				const index = keys[tableState?.currentPage].indexOf(record.uuid);
				return {
					...keys,
					[tableState?.currentPage]: [
						...keys[tableState?.currentPage].slice(0, index),
						...keys[tableState?.currentPage].slice(index + 1),
					],
				};
			});
		}
	};

	useEffect(() => {
		const canUpgradeDevices = [];
		Object.values(selectedRowKeys)
			.flat()
			.forEach((device) => {
				let deviceData = undefined;
				for (const deviceList of Object.values(data)) {
					const dev = deviceList.find((d) => d.uuid === device);
					if (dev) {
						deviceData = dev;
						break;
					}
				}
				const isServerless = getIsServerless(deviceData?.producer);
				if (!deviceData || isServerless) return true;
				const upgradeAvailable = getFirmwareStatus({
					device: deviceData,
					softwareAvailable,
				});
				deviceData?.status !== DeviceStatusEnum.Offline &&
					deviceData?.status !== DeviceStatusEnum.Recording &&
					(upgradeAvailable.status === FirmwareStatus.UPGRADE ||
						upgradeAvailable.status === FirmwareStatus.UPGRADE_FAILED) &&
					canUpgradeDevices.push(device);
			});
		setUpgradeDevices(canUpgradeDevices);
		setSelectionCount(
			Object.values(selectedRowKeys).reduce(
				(acc, selection) => acc + selection.length,
				0
			)
		);
	}, [Object.values(selectedRowKeys).flat().length]);

	useEffect(() => {
		setSelectionCount(
			Object.values(selectedRowKeys).reduce(
				(acc, selection) => acc + selection.length,
				0
			)
		);
	}, [tableState?.currentPage]);

	const rowSelection = {
		selectedRowKeys: selectedRowKeys[tableState?.currentPage] ?? [],
		onSelect: handleSelect,
		columnTitle: ' ',
	};

	const toggleSelectAll = () => {
		setSelectedRowKeys((keys) =>
			keys[tableState?.currentPage]?.length ===
			data[tableState?.currentPage].length
				? { ...keys, [tableState?.currentPage]: [] }
				: {
						...keys,
						[tableState?.currentPage]: data[tableState?.currentPage].map(
							(r) => r.uuid
						),
					}
		);
	};

	const handleUpgrade = (action: 'single' | 'multi') => {
		const upgradeAction = {
			single: () => {
				cameraFirmwareAction[FirmwareStatus.UPGRADE]({
					cameraUUID: pairedDeviceUUID.current,
				});
				setOpenModal({ open: false, type: 'single' });
			},
			multi: () => {
				setOpenModal({ open: false, type: 'multi' });
				Boolean(upgradeDevices.length) &&
					cameraFirmwareAction[FirmwareAction.BULK_UPGRADE](upgradeDevices);
				setSelectedRowKeys({ 1: [] });
				setUpgradeDevices([]);
			},
		};
		upgradeAction[action]();
	};

	const isAllSelectedByPage =
		selectedRowKeys[tableState?.currentPage]?.length ===
		data[tableState?.currentPage]?.length;

	const performSearch = useDebouncedCallback((value: string) => {
		setTableState((prev) => ({
			...prev,
			search: value,
			status: value ? getMatchingStatusIds(value, t) : [],
			currentPage: 1,
		}));
	}, 300);

	const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchInputValue(e.target.value);
		performSearch(e.target.value);
	};

	return (
		<div className={styles.wrapperTable}>
			<div className={styles.header}>
				<Title level={3} className={styles.title}>
					{t('title')} <div className={styles.circle}>{total}</div>
				</Title>
				<div>
					<Form
						name="search-form"
						fields={[{ name: ['cameraSearch'], value: searchInputValue }]}
					>
						<Form.Item name="cameraSearch">
							<SearchInput
								placeholder={t('searchBarPlaceholder')}
								onChange={onSearch}
							/>
						</Form.Item>
					</Form>
				</div>
				<Button
					type="primary"
					aria-label={t('pairButton')}
					onClick={() => setPairingModalState(true)}
				>
					<Icon component={IconNames.PLUS} size={24} />
					{t('pairButton')}
				</Button>
			</div>
			<NotificationModal
				titleIconType={
					softwareInstallationState === DeployStatusEnum.Succeed
						? IconType.OK
						: IconType.KO
				}
				title={
					softwareInstallationState === DeployStatusEnum.Succeed
						? t('installationModal.success.title')
						: t('installationModal.error.title')
				}
				body={
					<p>
						{softwareInstallationState === DeployStatusEnum.Succeed ? (
							t('installationModal.success.explanation')
						) : (
							<Trans
								i18nKey="cameras:installationModal.error.explanation"
								components={[
									<Link key="mail-support" href="mailto:support@automatic.tv">
										{t('supportEmail')}
									</Link>,
								]}
							/>
						)}
					</p>
				}
				open={currentStep?.index === PAIRING_STEPS.DEPLOY_STATUS}
				onOk={() => {
					softwareInstallationState === DeployStatusEnum.Succeed
						? setCurrentStep((prevStep) => ({
								index: prevStep.index + 1,
								component: null,
								title: null,
								isCompleted() {
									return true;
								},
							}))
						: setCurrentStep(pairingSteps[1]);
				}}
				width={438}
				centered
				footer={null}
				closable={true}
				onCancel={() => {
					setCurrentStep(pairingSteps[1]);
					setPairingModalState(false);
				}}
				footerBtns={[
					<Button
						key={0}
						type="ghost"
						aria-label="Error"
						onClick={() => {
							softwareInstallationState === DeployStatusEnum.Succeed
								? setCurrentStep((prevStep) => ({
										index: prevStep.index + 1,
										component: null,
										title: null,
										isCompleted() {
											return true;
										},
									}))
								: deploySoftwareInstallation();
						}}
					>
						{softwareInstallationState === DeployStatusEnum.Succeed
							? t('installationModal.success.confirm')
							: t('installationModal.error.confirm')}
					</Button>,
				]}
			/>
			<Table
				className={styles.table}
				columns={columns}
				sortDirections={['ascend', 'descend']}
				showSorterTooltip={false}
				onChange={(pagination: TablePaginationConfig, _, sorter) => {
					const sorterMod = sorter as SorterResult<DeviceDTO>;
					const direction = sorterMod?.order
						? sorterMod.order === 'ascend'
							? 'asc'
							: 'desc'
						: undefined;
					const activeField =
						direction && sorterMod?.field
							? (sorterMod.field as string)
							: undefined;

					setTableState((prev) => {
						const sortChanged =
							activeField !== prev.sortBy || direction !== prev.sortDir;
						return {
							...prev,
							sortBy: activeField,
							sortDir: direction,
							currentPage: sortChanged
								? 1
								: (pagination.current ?? prev.currentPage),
							pageSize: pagination.pageSize ?? prev.pageSize,
						};
					});
				}}
				dataSource={data[tableState?.currentPage]}
				rowClassName={styles.tableRow}
				rowKey={(record) => record.uuid}
				onRow={(record) => ({
					onClick: () => {
						setToSessionStorage('device_uuid', record.uuid);
						accessToCamDetails(record.uuid);
						router.push({
							pathname: '/camera/[device_uuid]/details',
							query: { device_uuid: record.uuid },
						});
					},
				})}
				rowSelection={{
					preserveSelectedRowKeys: true,
					...rowSelection,
				}}
				pagination={{
					position: ['bottomCenter'],
					total,
					current: tableState?.currentPage,
					pageSize: tableState?.pageSize,
					showTotal: (total, range) => (
						<span
							style={{
								position: 'absolute',
								left: '50%',
								transform: 'translateX(-50%)',
								bottom: '48px',
							}}
						>
							{t('pagination1')} {range[0]}-{range[1]} {t('pagination2')}{' '}
							{total}
						</span>
					),
					showLessItems: true,
					showSizeChanger: false,
				}}
				data-testid="cameras-list"
				{...(total > 0 && {
					footer: () => (
						<CameraListFooter
							data={data[tableState?.currentPage]}
							handleSelectAll={toggleSelectAll}
							handleSelectedKeys={setSelectedRowKeys}
							handleUpgrade={() => setOpenModal({ open: true, type: 'multi' })}
							selectedRowKeys={selectedRowKeys[tableState?.currentPage]}
							checkAllSelected={isAllSelectedByPage}
							selectionCount={selectionCount}
							disableUpgrade={!upgradeDevices.length}
						/>
					),
				})}
			/>
			<ConfirmationModal
				modalState={openModal.open}
				confirmTitleText={t('confirmationModal.title')}
				confirmQuestionText={
					openModal.type === 'single'
						? t('confirmationModal.question')
						: t('bulkConfirmationModal.question', {
								count: upgradeDevices.length,
							})
				}
				explanationText={t('confirmationModal.explanation')}
				confirmGuideText={t('confirmationModal.confirmGuide')}
				cancelText={t('confirmationModal.cancel')}
				confirmText={t('confirmationModal.start')}
				closeCallback={() => setOpenModal({ open: false, type: 'single' })}
				confirmCallback={() => handleUpgrade(openModal.type)}
			/>
			{total === 0 && (
				<div className={styles.noData}>
					<Image
						src={'/img/no-data.png'}
						alt="No cameras"
						width={217}
						height={178}
					/>
					<p className={styles.noDataTitle}>{t('noData.title')}</p>
					<p>{t('noData.desc')}</p>
				</div>
			)}
		</div>
	);
}

export default CamerasList;
